<template>
  <app-layout>
    <template v-slot:header>
      <AppHead :title="`Edit Product ${form.name}`" />
      <div class="d-flex justify-content-end">
        <h2 class="mr-3 w-100">Edit Product {{ form.name }}</h2>
        <div class="d-flex flex-column flex-lg-row">
          <InputSwitch v-model="product.active" @change.stop="active(product)" />
          <p class="mb-0 ml-0 ml-lg-2 d-flex align-items-center">Active</p>
        </div>
        <flash-messages />
      </div>
    </template>
        
    <template v-slot:default>
      <div class="row py-3">
        <div class="col-12">
          <trashed-message v-if="product.deleted_at" class="mb-6" @restore="restore">
            This product has been deleted.
          </trashed-message>
          <jet-button icon="fas fa-images" class="mb-3" @click="manageImages()">Manage Images</jet-button>

          <form @submit.prevent="update">
            <div class="row">
              <div class="form-group col-sm-4">
                <jet-label for="name" value="Name" />
                <jet-input id="name" type="text" class="form-control" v-model="form.name" autocomplete="name" />
                <jet-input-error :message="form.errors.name" class="mt-2" />
              </div>
              <div class="form-group col-sm-4">
                <jet-label for="sku" value="SKU" />
                <jet-input id="sku" type="text" class="form-control" v-model="form.sku" autocomplete="sku" />
                <jet-input-error :message="form.errors.sku" class="mt-2" />
              </div>
              <div class="form-group col-sm-4">
                <jet-label for="manufacturer" value="Manufacturer" />
                <jet-input id="manufacturer" type="text" class="form-control" v-model="form.manufacturer" autocomplete="manufacturer" />
                <jet-input-error :message="form.errors.manufacturer" class="mt-2" />
              </div>
            </div>

            <div class="row">
              <div class="form-group col-sm-6">
                <jet-label for="short_description" value="Short Description" />
                <textarea id="short_description"  class="form-control" v-model="form.short_description" ></textarea>
                <jet-input-error :message="form.errors.short_description" class="mt-2" />
              </div>
              <div class="form-group col-sm-6">
                <jet-label for="long_description" value="Long Description" />
                <ckeditor id="long_description" :editor="editor"  v-model="form.long_description" :config="editorConfig"></ckeditor>
                <jet-input-error :message="form.errors.long_description" class="mt-2" />
              </div>
            </div>

            <div class="row">
              <div class="form-group col-sm-4">
                <jet-label for="price_msrp" value="Pricing - MSRP" />
                <jet-input id="price_msrp"  type="number" step=".01" class="form-control" v-model="form.price_msrp" />
                <jet-input-error :message="form.errors.price_msrp" class="mt-2" />
              </div>
              <div class="form-group col-sm-4">
                <jet-label for="wholesale_price_fixed" value="Pricing - Wholesale" />
                <jet-input id="wholesale_price_fixed"  type="number" step=".01" class="form-control" v-model="form.wholesale_price_fixed" />
                <jet-input-error :message="form.errors.wholesale_price_fixed" class="mt-2" />
              </div>
              <div class="form-group col-sm-4">
                <jet-label for="user_price" value="Pricing - User" />
                <jet-input id="user_price"  type="number" step=".01" class="form-control" v-model="form.user_price" />
                <jet-input-error :message="form.errors.user_price" class="mt-2" />
              </div>
            </div>

            <div class="bottom-button">
              <jet-button class="px-5" btnStyle="success" :disable="form.processing" type="submit">Edit Product</jet-button>
            </div>
          </form>
        </div>
      </div>
    </template>
  </app-layout>
</template>

<script>

import AppLayout from '@/Layouts/AppLayout'
import JetButton from '@/Jetstream/Button'
import JetFormSection from '@/Jetstream/FormSection'
import JetInput from '@/Jetstream/Input'
import JetCheckbox from '@/Jetstream/Checkbox'
import JetInputError from '@/Jetstream/InputError'
import JetLabel from '@/Jetstream/Label'
import JetActionMessage from '@/Jetstream/ActionMessage'
import FlashMessages from '@/Shared/FlashMessages'
import InputSwitch from 'primevue/inputswitch';
import TrashedMessage from '@/Shared/TrashedMessage'

import CKEditor from '@ckeditor/ckeditor5-vue';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import UploadAdapter from '@/UploadAdapter';

export default {
  metaInfo() {
    return {
      title: `Edit Product: ${this.form.name}`,
    }
  },
  components: {
    JetActionMessage,
    JetButton,
    JetFormSection,
    JetInput,
    JetCheckbox,
    JetInputError,
    JetLabel,
    AppLayout,
    FlashMessages,
    InputSwitch,
    TrashedMessage,
    ckeditor: CKEditor.component
  },
  props: {
    product: Object,
    manufacturers: Array,
    warehouses: Array
  },
  remember: 'form',
  data() {
    return {
      editor: ClassicEditor,
      editorConfig: {
        extraPlugins: [this.uploader],
        product: this.product
      },
      form: this.$inertia.form({
      _method: 'put',
      name: this.product.name,
      sku: this.product.sku,
      manufacturer: this.product.manufacturer,
      short_description: this.product.short_description,
      long_description: this.product.long_description,
      price_msrp: this.product.price_msrp,
      user_price: this.product.user_price,  
      wholesale_price_fixed: this.product.wholesale_price_fixed
      }),
      productForm: this.$inertia.form({
      _method: 'put',
      active: null,
      }),
      processing: false
    }
  },
  methods: {
    update() {
      this.form.post(this.route('mtd.products.update', this.product.id), {
        onSuccess: () => this.form.reset('password', 'photo'),
      })
    },
    destroy() {
      if (confirm('Are you sure you want to delete this product?')) {
        this.$inertia.delete(this.route('products.destroy', this.product.id))
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this product?')) {
        this.$inertia.put(this.route('products.restore', this.product.id))
      }
    },
    manageImages() {
        this.$inertia.visit(this.route('mtd.products.images', this.product.id));
    },
    uploader(editor) {
      editor.plugins.get( 'FileRepository' ).createUploadAdapter = ( loader ) => {
          return new UploadAdapter( loader, editor.config._config.product );
      }
    },
    active(product) {
      this.productForm.active = product.active ? 1 : 0;
      this.productForm.post(this.route('mtd.products.active', product.id), { preserveScroll: true })
    }
  },
}
</script>
